import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Save, RefreshCw, Loader2, History, ArrowRight, UserCog, Zap, Sparkles } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import SearchableDriverSelect from './SearchableDriverSelect';
import StopProgressCard from './StopProgressCard';
import ProfitabilityPanel from './ProfitabilityPanel';

import { LOAD_STATUSES, LOAD_STATUS_LABELS, STATUSES_REQUIRE_REASON, statusLabel } from '../lib/loadStatus';
import LinkDriverModal from './LinkDriverModal';
import { Link2Off } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_STATUSES = LOAD_STATUSES.map((value) => ({ value, label: LOAD_STATUS_LABELS[value] }));

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const eventLabel = (e) => {
  switch (e.event_type) {
    case 'created': return 'Load created';
    case 'updated': return 'Load edited';
    case 'reassigned': return 'Driver reassigned';
    case 'status_changed': return `Status: ${e.details?.from_status || '?'} → ${e.details?.to_status || '?'}`;
    case 'document_uploaded': return `Document uploaded (${e.details?.doc_kind || ''})`;
    case 'document_deleted': return `Document deleted (${e.details?.doc_kind || ''})`;
    default: return e.event_type || 'Event';
  }
};

/**
 * Modal to edit a load: change status (with reopen support), reassign driver,
 * and view audit history. Admin/Dispatcher: full power. Driver: only view + notes.
 */
const LoadEditModal = ({ load, drivers, role, open, onClose, onUpdated }) => {
  const [status, setStatus] = useState(load?.status || 'assigned');
  const [reason, setReason] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [recovery, setRecovery] = useState({ recovery_date: '', replacement_driver_id: '', replacement_carrier: '' });
  const [driverId, setDriverId] = useState(load?.driver_id || '');
  const [coDriverId, setCoDriverId] = useState(load?.co_driver_id || '');
  const [notes, setNotes] = useState(load?.notes || '');
  // Override the actual delivery datetime — admins use this when a driver drops
  // early and should be paid in a prior week. datetime-local format for <input>.
  const _toLocalDT = (v) => {
    try {
      const d = new Date(v);
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    } catch { return ''; }
  };
  const _initialDeliveredAt = load?.delivered_at ? _toLocalDT(load.delivered_at) : '';
  const [deliveredAt, setDeliveredAt] = useState(_initialDeliveredAt);

  // Admin-editable core details (all map to existing load fields / PUT endpoint).
  const _initDetails = () => ({
    load_ref: load?.load_ref || '',
    load_rate: load?.load_rate != null ? String(load.load_rate) : '',
    broker: load?.broker || '',
    pickup_date: load?.pickup_date || '',
    delivery_date: load?.delivery_date || '',
    pickup_city: load?.pickup_city || '',
    pickup_state: load?.pickup_state || '',
    delivery_city: load?.delivery_city || '',
    delivery_state: load?.delivery_state || '',
    loaded_miles: load?.loaded_miles != null ? String(load.loaded_miles) : '',
    total_miles: load?.total_miles != null ? String(load.total_miles) : '',
  });
  const [details, setDetails] = useState(_initDetails());
  const setDetail = (k, v) => setDetails((d) => ({ ...d, [k]: v }));
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [bookingContext, setBookingContext] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCached, setAiCached] = useState(false);
  const [aiGeneratedAt, setAiGeneratedAt] = useState(null);

  const canEditAll = role === 'super_admin' || role === 'company_admin' || role === 'dispatcher';

  useEffect(() => {
    if (!load) return;
    setStatus(load.status);
    setDriverId(load.driver_id || '');
    setCoDriverId(load.co_driver_id || '');
    setDetails(_initDetails());
    setNotes(load.notes || '');
    if (load.delivered_at) {
      setDeliveredAt(_toLocalDT(load.delivered_at));
    } else {
      setDeliveredAt('');
    }
  }, [load?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !load?.id) return;
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/loads/${load.id}/history`, { withCredentials: true });
        if (mounted) setHistory(r.data || []);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [open, load?.id]);

  // Fetch the full Booking Score (with customer/lane/fleet context). Lightweight,
  // single request per modal-open, gated by the presence of computed profitability.
  useEffect(() => {
    if (!open || !load?.id || !load?.profitability) {
      setBookingContext(null);
      setAiAnalysis(null);
      setAiCached(false);
      setAiGeneratedAt(null);
      return;
    }
    let mounted = true;
    setBookingLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/profitability/booking-score/${load.id}`, { withCredentials: true });
        if (mounted) setBookingContext(r.data || null);
      } catch {
        if (mounted) setBookingContext(null);
      } finally {
        if (mounted) setBookingLoading(false);
      }
    })();
    // Fire AI analysis in parallel — it's slower (3-5s) but non-blocking.
    setAiLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/profitability/ai-analysis/${load.id}`, { withCredentials: true });
        if (mounted) {
          setAiAnalysis(r.data?.analysis || null);
          setAiCached(!!r.data?.cached);
          setAiGeneratedAt(r.data?.generated_at || null);
        }
      } catch {
        if (mounted) setAiAnalysis(null);
      } finally {
        if (mounted) setAiLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, load?.id, load?.profitability]);

  const reanalyze = async () => {
    if (!load?.id || aiLoading) return;
    setAiLoading(true);
    try {
      const r = await axios.get(`${API}/profitability/ai-analysis/${load.id}?force=true`, { withCredentials: true });
      setAiAnalysis(r.data?.analysis || null);
      setAiCached(false);
      setAiGeneratedAt(r.data?.generated_at || null);
    } catch {
      // keep prior text on failure
    } finally {
      setAiLoading(false);
    }
  };

  if (!open || !load) return null;

  const wasDelivered = load.status === 'delivered';
  const canForceComplete = canEditAll && !wasDelivered;

  const forceComplete = async () => {
    const reason = window.prompt('Force-complete reason (visible in audit log + sent to admins/dispatch):', '');
    if (reason === null) return; // cancelled
    setBusy(true);
    try {
      await axios.post(
        `${API}/loads/${load.id}/force-complete`,
        { reason: reason || null },
        { withCredentials: true }
      );
      toast.success('Load force-completed');
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to force-complete');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const updates = [];
      if (canEditAll) {
        // 1. Status change (reopen supported)
        if (status !== load.status) {
          if (STATUSES_REQUIRE_REASON.includes(status) && !reason.trim()) {
            toast.error(`A reason is required to mark this load ${statusLabel(status)}.`);
            setBusy(false);
            return;
          }
          const body = { status, reason: reason.trim() || undefined };
          if (status === 'recovered') Object.assign(body, { recovery_date: recovery.recovery_date || undefined, replacement_driver_id: recovery.replacement_driver_id || undefined, replacement_carrier: recovery.replacement_carrier || undefined });
          updates.push(axios.post(`${API}/loads/${load.id}/status`, body, { withCredentials: true }));
        }
        // 2. Reassign driver
        if (driverId && driverId !== load.driver_id) {
          updates.push(axios.post(`${API}/loads/${load.id}/assign`, { driver_id: driverId }, { withCredentials: true }));
        }
        // 3. Notes
        if ((notes || '') !== (load.notes || '')) {
          updates.push(axios.put(`${API}/loads/${load.id}`, { notes }, { withCredentials: true }));
        }
        // 3a. Co-driver (team) change
        if ((coDriverId || '') !== (load.co_driver_id || '')) {
          updates.push(axios.put(`${API}/loads/${load.id}`, { co_driver_id: coDriverId || '' }, { withCredentials: true }));
        }
        // 3b. Core detail edits — admins can change any load info on any status.
        const detailPayload = {};
        Object.keys(details).forEach((k) => {
          const orig = load?.[k] != null ? String(load[k]) : '';
          if ((details[k] || '') !== orig) detailPayload[k] = details[k];
        });
        if (Object.keys(detailPayload).length) {
          updates.push(axios.put(`${API}/loads/${load.id}`, detailPayload, { withCredentials: true }));
        }
        // 4. Delivered-at override — only meaningful when the load is or
        // will be delivered. Compare ISO date strings to avoid timezone drift.
        const willBeDelivered = (status || load.status) === 'delivered';
        if (willBeDelivered && deliveredAt && deliveredAt !== _initialDeliveredAt) {
          const [_d, _t] = deliveredAt.split('T');
          updates.push(axios.patch(
            `${API}/loads/${load.id}/delivered-at`,
            { delivered_at: _d, delivered_time: _t || null },
            { withCredentials: true },
          ));
        }
      } else if (role === 'driver') {
        // Driver: status (within allowed transitions) + notes
        if (status !== load.status) {
          if (STATUSES_REQUIRE_REASON.includes(status) && !reason.trim()) {
            toast.error(`A reason is required to mark this load ${statusLabel(status)}.`);
            setBusy(false);
            return;
          }
          const body = { status, reason: reason.trim() || undefined };
          if (status === 'recovered') Object.assign(body, { recovery_date: recovery.recovery_date || undefined, replacement_driver_id: recovery.replacement_driver_id || undefined, replacement_carrier: recovery.replacement_carrier || undefined });
          updates.push(axios.post(`${API}/loads/${load.id}/status`, body, { withCredentials: true }));
        }
        if ((notes || '') !== (load.notes || '')) {
          updates.push(axios.put(`${API}/loads/${load.id}`, { notes }, { withCredentials: true }));
        }
      }

      for (const u of updates) await u;
      toast.success(wasDelivered && status !== 'delivered' ? 'Load reopened' : 'Load updated');
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update load');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="load-edit-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Correct / Update load
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {load.load_ref || load.id.slice(-6)} • {load.pickup_location} → {load.delivery_location}
            </p>
          </div>
          <button onClick={onClose} data-testid="load-edit-close" className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Status */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Status {wasDelivered && status !== 'delivered' && <span className="ml-2 text-[#FF6B00] normal-case">— reopening</span>}
            </label>
            <select
              data-testid="load-edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {ALL_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {canEditAll && STATUSES_REQUIRE_REASON.includes(status) && status !== load?.status && (
              <div className="mt-2 space-y-2" data-testid="load-status-reason-block">
                <input data-testid="load-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} required placeholder={`Reason for ${statusLabel(status)} (required)`} className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-amber-50" />
                {status === 'recovered' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input data-testid="load-recovery-date" type="date" value={recovery.recovery_date} onChange={(e) => setRecovery({ ...recovery, recovery_date: e.target.value })} className="px-2 py-2 border border-slate-300 rounded-md text-sm" title="Recovery date" />
                    <select data-testid="load-recovery-driver" value={recovery.replacement_driver_id} onChange={(e) => setRecovery({ ...recovery, replacement_driver_id: e.target.value })} className="px-2 py-2 border border-slate-300 rounded-md text-sm bg-white">
                      <option value="">Replacement driver (optional)</option>
                      {(drivers || []).map((d) => <option key={d.user_id || d.id} value={d.user_id || d.id}>{d.name}</option>)}
                    </select>
                    <input data-testid="load-recovery-carrier" value={recovery.replacement_carrier} onChange={(e) => setRecovery({ ...recovery, replacement_carrier: e.target.value })} placeholder="Outside carrier (optional)" className="px-2 py-2 border border-slate-300 rounded-md text-sm" />
                  </div>
                )}
                <p className="text-[11px] text-amber-800">Canceled / Rejected / Recovered loads never count as Delivered and never pay normal delivery compensation; authorized TONU/detention stays via payroll adjustments. Recovered loads keep their revenue and history; a recovery completed by an outside carrier does not pay the original driver.</p>
              </div>
            )}
            {canEditAll && (load?.status_history?.length > 0) && (
              <details className="mt-2 text-[11px] text-slate-600" data-testid="load-status-history">
                <summary className="cursor-pointer font-semibold">Status history ({load.status_history.length})</summary>
                <ul className="mt-1 space-y-0.5">{[...load.status_history].reverse().map((h, i) => <li key={i}>{statusLabel(h.from_status)} → <b>{statusLabel(h.to_status)}</b> · {h.user_name} · {h.at ? new Date(h.at).toLocaleString() : ''}{h.reason ? ` · ${h.reason}` : ''}</li>)}</ul>
              </details>
            )}
            {canEditAll && load?.driver_unlinked && (
              <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between gap-3" data-testid="load-edit-unlinked-panel">
                <div className="text-xs text-slate-700">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-200 text-slate-700 mr-2"><Link2Off className="w-3 h-3" /> Unlinked</span>
                  Imported driver name: <b>{load.unmatched_driver_name}</b>
                </div>
                <button type="button" data-testid="load-edit-link-driver" onClick={() => setLinkOpen(true)} className="px-3 py-1.5 text-xs font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00247D]">Link driver</button>
              </div>
            )}
            {linkOpen && <LinkDriverModal load={load} onClose={() => setLinkOpen(false)} onLinked={() => { onUpdated?.(); onClose?.(); }} />}
            {canEditAll && (
              <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                As {role}, you can move this load to any status, including reopening completed loads.
              </p>
            )}
          </div>

          {/* Delivered-at override — only for admins on delivered loads. */}
          {canEditAll && status === 'delivered' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Delivered on (date &amp; time)
                <span className="ml-2 text-[10px] normal-case tracking-normal text-slate-400">— used for payroll period filter</span>
              </label>
              <input
                type="datetime-local"
                data-testid="load-edit-delivered-at"
                value={deliveredAt}
                onChange={(e) => setDeliveredAt(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
              <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Override the actual drop date &amp; time — if a driver delivered early or a load was marked delivered on the wrong date/time, set the correct timestamp so it lands in the right payroll period.
              </p>
            </div>
          )}

          {/* Driver reassignment */}
          {canEditAll && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <UserCog className="w-3.5 h-3.5" />
                Assigned driver
              </label>
              <SearchableDriverSelect
                drivers={drivers}
                value={driverId}
                onChange={(uid) => setDriverId(uid)}
                testid="load-edit-driver-select"
              />
            </div>
          )}

          {/* Co-driver (team) */}
          {canEditAll && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <UserCog className="w-3.5 h-3.5" />
                Co-driver (team) <span className="text-slate-400 normal-case tracking-normal">— optional</span>
              </label>
              <SearchableDriverSelect
                drivers={(drivers || []).filter((d) => d.user_id !== driverId)}
                value={coDriverId}
                onChange={(uid) => setCoDriverId(uid)}
                placeholder="Search a second driver (team run)…"
                testid="load-edit-co-driver-select"
              />
              <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Primary driver is paid the load; the co-driver's share is handled via a Pay Transfer on payroll.
              </p>
            </div>
          )}

          {/* Admin: edit all load details (any status, incl. back-dating) */}
          {canEditAll && (
            <div className="border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Load details — edit any field
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['load_ref', 'Load ID#', 'text'],
                  ['load_rate', 'Load Rate ($)', 'text'],
                  ['broker', 'Broker', 'text'],
                  ['pickup_date', 'Pick Up Date', 'text'],
                  ['delivery_date', 'Delivery Date (backdate ok)', 'text'],
                  ['pickup_city', 'Pickup City', 'text'],
                  ['pickup_state', 'Pickup State', 'text'],
                  ['delivery_city', 'Delivery City', 'text'],
                  ['delivery_state', 'Delivery State', 'text'],
                  ['loaded_miles', 'Loaded Miles', 'text'],
                  ['total_miles', 'Total Miles', 'text'],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{label}</label>
                    <input
                      type="text"
                      value={details[key]}
                      onChange={(e) => setDetail(key, e.target.value)}
                      data-testid={`load-edit-${key}`}
                      placeholder={key.includes('date') ? 'MM-DD-YYYY' : ''}
                      className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Changes apply on Save regardless of load status. Delivery Date can be backdated for payroll/records.</p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Notes
            </label>
            <textarea
              value={notes || ''}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="load-edit-notes"
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              placeholder="Driver notes, correction reason, etc."
            />
          </div>

          {/* Stop Progress (read-only) */}
          <div data-testid="load-edit-stop-progress">
            <StopProgressCard loadId={load.id} readOnly />
          </div>

          {/* Profitability + Booking Score Panel */}
          {load.profitability && (
            <div data-testid="load-edit-profitability">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Profitability & Booking Score{bookingLoading && <Loader2 className="inline w-3 h-3 ml-2 animate-spin text-slate-400" />}
              </h3>
              <ProfitabilityPanel
                metrics={load.profitability}
                bookingContext={bookingContext}
                dense
              />
              {/* AI Analysis */}
              {(aiAnalysis || aiLoading) && (
                <div className="mt-3 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-md p-4" data-testid="ai-analysis-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">AI Load Analyst</span>
                    <span className="text-[10px] text-indigo-400">Claude Sonnet 4.5</span>
                    {aiCached && !aiLoading && (
                      <span className="text-[10px] text-indigo-500 italic" data-testid="ai-cached-badge">cached</span>
                    )}
                    {aiLoading && <Loader2 className="w-3 h-3 animate-spin text-indigo-400 ml-auto" />}
                    {!aiLoading && aiAnalysis && (
                      <button
                        type="button"
                        onClick={reanalyze}
                        data-testid="ai-reanalyze-btn"
                        className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                        title="Re-run Claude against the latest data"
                      >
                        <RefreshCw className="w-3 h-3" /> Re-analyze
                      </button>
                    )}
                  </div>
                  {aiLoading && !aiAnalysis ? (
                    <p className="text-xs text-indigo-700/70 italic" data-testid="ai-loading">Reading your fleet history…</p>
                  ) : (
                    <p className="text-sm leading-relaxed text-slate-800" data-testid="ai-analysis-text">{aiAnalysis}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* History */}
          <div>
            <button
              type="button"
              onClick={() => setShowHistory((s) => !s)}
              data-testid="load-edit-history-toggle"
              className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <History className="w-3.5 h-3.5" />
              {showHistory ? 'Hide' : 'Show'} change history ({history.length})
            </button>
            {showHistory && (
              <ol className="mt-3 space-y-2 border-l-2 border-slate-200 pl-4" data-testid="load-edit-history-list">
                {history.length === 0 ? (
                  <li className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No events yet.</li>
                ) : history.map((e) => (
                  <li key={e.id} className="text-xs text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <div className="flex items-center gap-1 text-slate-900 font-medium">
                      <ArrowRight className="w-3 h-3 text-[#002FA7]" />
                      {eventLabel(e)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      by {e.actor_name} {e.actor_role ? `(${e.actor_role})` : ''} • {fmtTime(e.created_at)}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
          <div>
            {canForceComplete && (
              <button
                type="button"
                onClick={forceComplete}
                disabled={busy}
                data-testid="load-edit-force-complete"
                className="px-3 py-2 text-xs text-[#FF6B00] border border-[#FF6B00]/30 rounded-md hover:bg-orange-50 disabled:opacity-50 flex items-center gap-1.5 font-semibold"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                title="Bypass odometer / asset checks and mark delivered now"
              >
                <Zap className="w-3.5 h-3.5" />
                Force complete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="load-edit-save"
              className="px-4 py-2 text-sm text-white bg-[#002FA7] rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2 font-semibold"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (wasDelivered && status !== 'delivered' ? <RefreshCw className="w-4 h-4" /> : <Save className="w-4 h-4" />)}
              {wasDelivered && status !== 'delivered' ? 'Reopen & save' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadEditModal;
